/**
 * NewsArticlePage — internal detail view for TBC-original articles.
 *
 * Behavior:
 *   - External RSS-ingested articles (with `source_url`) auto-redirect to the
 *     source in a new tab and show a small courtesy card in-page.
 *   - Internal original articles render the full `content` inline with author,
 *     category, image, and read-time.
 *   - 404 shows a branded not-found state that links back to /news.
 *
 * This closes a real user-facing bug: LandingPageV5's trending rail links to
 * /news/:id which previously 404'd because no route existed.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ExternalLink, Newspaper, Loader2, Clock, User } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SaveArticleButton from "@/components/SaveArticleButton";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

export default function NewsArticlePage() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const r = await axios.get(`${API}/articles/${articleId}`);
        if (!alive) return;
        setArticle(r.data);
        document.title = `${r.data.title} — The Blk Community`;
        // If this article is externally sourced, redirect to the original in
        // a new tab. We still render the courtesy card so the user has a
        // clear back-nav path if the pop-up was blocked.
        const src = r.data.source_url;
        if (src && /^https?:\/\//i.test(src)) {
          window.open(src, "_blank", "noopener,noreferrer");
        }
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [articleId]);

  return (
    <div className="min-h-screen bg-tbc-cream flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8">
        <Link
          to="/news"
          className="inline-flex items-center gap-1.5 text-sm text-tbc-navy/70 hover:text-tbc-navy mb-4"
          data-testid="news-article-back"
        >
          <ArrowLeft className="w-4 h-4" /> All news
        </Link>
        {article && (
          <div className="float-right -mt-1">
            <SaveArticleButton
              articleId={article.article_id}
              variant="chip"
              stopPropagation={false}
            />
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-tbc-navy/50">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading article…
          </div>
        )}

        {!loading && notFound && (
          <div className="text-center py-16" data-testid="news-article-not-found">
            <Newspaper className="w-10 h-10 text-tbc-gold/60 mx-auto" />
            <h1 className="font-heading text-2xl text-tbc-navy mt-3">Article not found</h1>
            <p className="text-sm text-tbc-navy/60 mt-2">
              This story may have been removed or the link is out of date.
            </p>
            <Button asChild className="mt-5 rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white">
              <Link to="/news">Browse all news</Link>
            </Button>
          </div>
        )}

        {!loading && article && article.source_url && (
          <div className="bg-white rounded-2xl border border-tbc-navy/10 p-6 shadow-sm" data-testid="news-article-external">
            {(article.image_url || article.image) && (
              <img
                src={article.image_url || article.image}
                alt={article.title}
                className="w-full aspect-video object-cover rounded-xl mb-5"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold bg-tbc-gold/20 text-tbc-navy">
              {article.category || "News"}
            </span>
            <h1 className="font-heading text-3xl text-tbc-navy mt-3">{article.title}</h1>
            {article.excerpt && (
              <p className="text-tbc-navy/75 mt-3 leading-relaxed">{article.excerpt}</p>
            )}
            <div className="mt-5 rounded-xl bg-tbc-cream/60 border border-tbc-navy/10 p-4 text-sm">
              <p className="text-tbc-navy/70">
                This story was originally published on an external site. If it didn&apos;t open automatically, tap below to read the full article.
              </p>
              <Button asChild className="mt-3 rounded-full bg-tbc-gold text-tbc-navy hover:bg-tbc-gold/90 font-semibold">
                <a
                  href={article.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="news-article-external-cta"
                >
                  Read full article <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        )}

        {!loading && article && !article.source_url && (
          <article className="bg-white rounded-2xl border border-tbc-navy/10 p-6 sm:p-10 shadow-sm" data-testid="news-article-internal">
            <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-semibold bg-tbc-gold/20 text-tbc-navy">
              {article.category || "News"}
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl text-tbc-navy mt-3 leading-tight">{article.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-tbc-navy/60">
              {article.author && (
                <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {article.author}</span>
              )}
              {article.read_time && (
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.read_time}</span>
              )}
            </div>
            {(article.image_url || article.image) && (
              <img
                src={article.image_url || article.image}
                alt={article.title}
                className="w-full aspect-video object-cover rounded-xl mt-6"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            {article.excerpt && (
              <p className="text-lg text-tbc-navy/80 mt-6 leading-relaxed">{article.excerpt}</p>
            )}
            {article.content && (
              <div
                className="prose prose-tbc mt-4 text-tbc-navy/85 leading-relaxed whitespace-pre-wrap"
                data-testid="news-article-content"
              >
                {article.content}
              </div>
            )}
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
}
